"use client";

import { RichTextRenderer } from "@scott/contentful/rich-text-renderer";
import type {
  ContentfulButton,
  ContentfulHeadlineBlock,
} from "@scott/contentful/types";
import { cva, type VariantProps } from "class-variance-authority";
import { Button } from "../../elements/button/button";
import { TextD2C } from "../../elements/text-d2c/text";
import { IconChevronRight } from "../../icons/chevron-right-icon";
import { Column, Container, Row } from "../../layout/grid";
import {
  ctaAttributes,
  promotionAttributes,
} from "../../lib/promotion-tracking";
import { cn } from "../../lib/utils";
import { DotsCarousel } from "../../patterns/carousel/dots-carousel";
import { VideoBlock } from "../video/video";
import { SLIDER_MAX_SLIDES } from "./slider.constants";
import type {
  SliderInspectorProps,
  SliderSlideInspectorProps,
} from "./slider.preview";

const MAX_SLIDE_BUTTONS = 2;

export interface SliderSlideImage {
  sys?: { id: string };
  src: string;
  srcSet?: string;
  mobileSrc?: string;
  mobileSrcSet?: string;
  alt: string;
  type?: "Image";
}

export interface SliderSlideVideo {
  sys?: { id: string };
  src: string;
  /** Required so an inactive video slide still has a stable visual preview. */
  poster: string;
  type: "Video";
}

export type SliderSlideMedia = SliderSlideImage | SliderSlideVideo;

export interface SliderSlide {
  sys?: { id: string };
  id: string;
  /** Display field of the linked Hero entry, shown by the live-preview tag. */
  internalName?: string;
  /** Cloudinary asset name of the slide image, for analytics. */
  creativeName?: string;
  media: SliderSlideMedia;
  title?: ContentfulHeadlineBlock;
  buttons?: ContentfulButton[];
  color?:
    | "black"
    | "white"
    | "alabastergrey"
    | "blazeorange"
    | "neonchartreuse";
  textHorizontalPosition?: "left" | "center";
  textVerticalPosition?: "top" | "center" | "bottom";
  inspectorProps?: SliderSlideInspectorProps;
}

const sliderBackgroundVariants = cva("", {
  variants: {
    backgroundColor: {
      black: "bg-black",
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
    },
  },
  defaultVariants: {
    backgroundColor: "white",
  },
});

const sliderColumnVariants = cva("", {
  variants: {
    columnWidth: {
      8: "col-span-10 col-start-2 md:col-span-8 md:col-start-3",
      10: "col-span-10 col-start-2",
      12: "col-span-12",
    },
  },
  defaultVariants: {
    columnWidth: 12,
  },
});

const slideContentVariants = cva("", {
  variants: {
    vertical: {
      top: "justify-start",
      center: "justify-center",
      bottom: "justify-end",
    },
  },
  defaultVariants: {
    vertical: "center",
  },
});

/** Keeps the overlay copy at a readable measure over the slide media. */
const slideTextColumnVariants = cva("", {
  variants: {
    horizontal: {
      left: "col-span-12 items-start text-left md:col-span-6",
      center:
        "col-span-12 items-center text-center md:col-span-8 md:col-start-3",
    },
  },
  defaultVariants: {
    horizontal: "left",
  },
});

/**
 * Darkens the media so the overlay copy stays legible. Follows the hero-banner
 * scrim idiom, turned horizontal and anchored to the side the copy sits on.
 */
const slideScrimVariants = cva("pointer-events-none absolute inset-y-0", {
  variants: {
    horizontal: {
      left: "left-0 w-2/3 bg-gradient-to-r from-black/70 to-transparent",
      center: "inset-x-0 bg-gradient-to-t from-black/70 to-transparent",
    },
  },
  defaultVariants: {
    horizontal: "left",
  },
});

/** Matches the contained hero ratio: portrait on mobile, 16:9 from md up. */
const slideRatioClasses = "aspect-1/2 md:aspect-video";

const dotNavigationClasses = cn(
  "-translate-x-1/2 absolute bottom-4 left-1/2 z-30 mt-0 gap-0 rounded-sm",
  "bg-black/40 px-1 py-0.5 backdrop-blur-[3px]",
  "md:bottom-8"
);

export interface SliderProps
  extends VariantProps<typeof sliderBackgroundVariants>,
    VariantProps<typeof sliderColumnVariants> {
  sys?: { id?: string };
  /** Display field of the entry, shown by the live-preview tag. */
  internalName?: string;
  /** Ordered slides. At most the first five are rendered. */
  slides: readonly SliderSlide[];
  columnWidth?: 8 | 10 | 12;
  /** Renders the container full width, without the fluid inner grid wrapper. */
  fullWidth?: boolean;
  backgroundColor?: "black" | "white" | "whitesmoke";
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  autoplay?: boolean;
  autoplayDelay?: number;
  ariaLabel?: string;
  goToSlideText?: string;
  slideNavigationText?: string;
  pauseAutoplayText?: string;
  resumeAutoplayText?: string;
  playVideoText?: string;
  pauseVideoText?: string;
  className?: string;
  inspectorProps?: SliderInspectorProps;
}

interface SlideMediaProps {
  backgroundColor: SliderProps["backgroundColor"];
  isActive: boolean;
  isPriority: boolean;
  media: SliderSlideMedia;
  pauseVideoText?: string;
  playVideoText?: string;
}

function SlideMedia(props: Readonly<SlideMediaProps>) {
  if (props.media.type === "Video") {
    return (
      // `isolate` keeps the player's own stacking (poster, loader) below the
      // slide content, which is a plain sibling without a z-index of its own.
      <div className="slider__video absolute inset-0 isolate overflow-hidden">
        {props.isActive ? (
          <VideoBlock
            background={props.backgroundColor}
            className="slider__video-player"
            fullWidth
            isBackgroundVideo
            paddingBottom="none"
            paddingTop="none"
            pauseBackgroundVideoText={props.pauseVideoText}
            playBackgroundVideoText={props.playVideoText}
            poster={props.media.poster}
            showBackgroundPlaybackControl
            src={props.media.src}
          />
        ) : (
          <img
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-center"
            loading="lazy"
            src={props.media.poster}
          />
        )}
      </div>
    );
  }

  const image = props.media;

  return (
    <picture className="slider__image absolute inset-0 overflow-hidden">
      {(image.mobileSrcSet || image.mobileSrc) && (
        <source
          media="(max-width: 767px), (orientation: portrait)"
          sizes="100vw"
          srcSet={image.mobileSrcSet || image.mobileSrc}
        />
      )}
      <img
        alt={image.alt}
        className="h-full w-full object-cover object-center"
        fetchPriority={props.isPriority ? "high" : undefined}
        loading={props.isPriority ? "eager" : "lazy"}
        sizes={image.srcSet ? "100vw" : undefined}
        src={image.src}
        srcSet={image.srcSet}
      />
    </picture>
  );
}

function SlideButtons(props: Readonly<SliderSlide>) {
  const buttons = props.buttons?.slice(0, MAX_SLIDE_BUTTONS);

  if (!buttons?.length) {
    return null;
  }

  return (
    <div className="mt-8 flex flex-row flex-wrap gap-4">
      {buttons.map((button, index) => {
        const inspectorKey = `button${index}` as "button0" | "button1";

        return (
          <span
            key={`${button.href}-${button.label}`}
            {...props.inspectorProps?.[inspectorKey]}
          >
            <Button
              className="font-brand"
              {...ctaAttributes(button)}
              href={button.href}
              size="lg"
              target={button.target}
              type="button"
              variant={button.variant ?? "default"}
            >
              {button.label}
              <IconChevronRight />
            </Button>
          </span>
        );
      })}
    </div>
  );
}

function hasSlideContent(slide: SliderSlide): boolean {
  return Boolean(
    slide.title?.eyebrow ||
      slide.title?.headline ||
      slide.title?.subline ||
      slide.buttons?.length
  );
}

function SlideContent(props: Readonly<SliderSlide>) {
  const { title } = props;

  if (!hasSlideContent(props)) {
    return null;
  }

  return (
    <div
      className={cn(
        "slider__content pointer-events-none absolute inset-0 flex flex-col py-small md:py-medium",
        slideContentVariants({ vertical: props.textVerticalPosition })
      )}
    >
      <Row className="px-small md:px-medium">
        <Column
          className={cn(
            "pointer-events-auto flex flex-col",
            slideTextColumnVariants({
              horizontal: props.textHorizontalPosition,
            })
          )}
        >
          {title?.eyebrow && (
            <TextD2C
              color={props.color}
              variant="pillar"
              {...props.inspectorProps?.eyebrow}
            >
              {title.eyebrow}
            </TextD2C>
          )}

          {title?.headline && (
            <TextD2C
              color={props.color}
              dash
              style="no-mb"
              variant="h2"
              {...props.inspectorProps?.title}
            >
              <RichTextRenderer richText={title.headline.json} />
            </TextD2C>
          )}

          {title?.subline && (
            <TextD2C
              color={props.color}
              variant="p"
              {...props.inspectorProps?.subline}
            >
              {title.subline}
            </TextD2C>
          )}

          <SlideButtons {...props} />
        </Column>
      </Row>
    </div>
  );
}

interface SliderSlideViewProps {
  backgroundColor: SliderProps["backgroundColor"];
  isActive: boolean;
  isPriority: boolean;
  pauseVideoText?: string;
  playVideoText?: string;
  position: number;
  slide: SliderSlide;
}

function SliderSlideView(props: Readonly<SliderSlideViewProps>) {
  return (
    <div
      className={cn("relative overflow-hidden", slideRatioClasses)}
      {...promotionAttributes(
        props.slide.internalName
          ? {
              name: props.slide.internalName,
              creativeName: props.slide.creativeName,
            }
          : undefined,
        "slider",
        props.position
      )}
      {...props.slide.inspectorProps?.container}
    >
      <SlideMedia
        backgroundColor={props.backgroundColor}
        isActive={props.isActive}
        isPriority={props.isPriority}
        media={props.slide.media}
        pauseVideoText={props.pauseVideoText}
        playVideoText={props.playVideoText}
      />
      {hasSlideContent(props.slide) && (
        <div
          className={cn(
            "slider__scrim",
            slideScrimVariants({
              horizontal: props.slide.textHorizontalPosition,
            })
          )}
          data-testid="slider-scrim"
        />
      )}
      <SlideContent {...props.slide} />
    </div>
  );
}

function SliderCarousel(props: Readonly<SliderProps>) {
  const slides = props.slides.slice(0, SLIDER_MAX_SLIDES);

  if (slides.length === 1) {
    return (
      <div data-testid="slider-slide">
        <SliderSlideView
          backgroundColor={props.backgroundColor}
          isActive
          isPriority
          pauseVideoText={props.pauseVideoText}
          playVideoText={props.playVideoText}
          position={1}
          slide={slides[0]}
        />
      </div>
    );
  }

  return (
    <DotsCarousel
      ariaLabel={props.ariaLabel ?? "Content slider"}
      autoplay={props.autoplay}
      autoplayDelay={props.autoplayDelay}
      dotNavigationClassName={dotNavigationClasses}
      goToSlideText={props.goToSlideText}
      pauseAutoplayText={props.pauseAutoplayText}
      renderSlide={(slide, { index, isActive }) => (
        <SliderSlideView
          backgroundColor={props.backgroundColor}
          isActive={isActive}
          isPriority={index === 0}
          pauseVideoText={props.pauseVideoText}
          playVideoText={props.playVideoText}
          position={index + 1}
          slide={slide}
        />
      )}
      resumeAutoplayText={props.resumeAutoplayText}
      slideNavigationText={props.slideNavigationText}
      slides={slides}
      slideTestId="slider-slide"
      trackingName={props.internalName}
    />
  );
}

export function Slider(props: Readonly<SliderProps>) {
  if (props.slides.length === 0) {
    return null;
  }

  return (
    <Container
      className={cn(
        "component-slider",
        sliderBackgroundVariants({ backgroundColor: props.backgroundColor }),
        props.className
      )}
      fullWidth
      outerContainerOnly={props.fullWidth}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      testId="slider"
      {...props.inspectorProps?.container}
    >
      <Row className="gap-0">
        <Column
          className={sliderColumnVariants({ columnWidth: props.columnWidth })}
        >
          <div data-testid="slider-slides" {...props.inspectorProps?.slides}>
            <SliderCarousel {...props} />
          </div>
        </Column>
      </Row>
    </Container>
  );
}
